import { CacheLookupPolicy, type AccountInfo } from '@azure/msal-browser';
import { jwtDecode } from 'jwt-decode';

import { authService, registrarRecuperacaoDe401, SESSAO_ENTRA_KEY } from 'modules/auth/services/authService';
import { loginRequest, msalInstance } from 'modules/auth/services/msalConfig';
import { aguardarReautenticacao } from 'modules/auth/services/reautenticacao';
import type { AuthSessao, LoginResponse } from 'types/domain';

// Renovação da sessão pelo Entra.
//
// O login com Microsoft troca o id_token do Entra por um JWT do Rocky de validade fixa (12h, no
// TokenService). Aqui o JWT é trocado por outro antes de vencer, como em qualquer SPA com MSAL: quem
// usa todo dia não cai, e só entra de novo quando a sessão do Entra acabar (política do tenant).
//
// 1. Silencioso: refresh token do MSAL e, quando ele vence (24h desde o login, prazo fixo para SPA),
//    iframe oculto com a sessão do Entra. O id_token novo vira JWT em POST /api/auth/sso/exchange.
//    O iframe depende de duas coisas: o /auth/callback repassar a resposta pelo redirect-bridge do
//    MSAL v5 (index.tsx) e o staticwebapp.config.json deixar o próprio site em frame
//    (X-Frame-Options SAMEORIGIN).
// 2. Popup, no "Continuar conectado" do aviso de expiração: quando o silencioso falha com o JWT ainda
//    valendo. É o caminho do Safari, que bloqueia o cookie do Entra dentro do iframe. A página não
//    recarrega, então nada do que está sendo digitado se perde.
// 3. Redirect com prompt=none: JWT já vencido ao abrir o app ou ao voltar para a aba. Com a sessão
//    do Entra viva, volta direto para a mesma rota; se o Entra pedir interação, cai na tela de login.
//
// Um 401 numa chamada passa por aqui também (recuperarSessaoApos401, registrada no authService): a
// renovação roda à força e, se não der, o diálogo de sessão encerrada (reautenticacao.ts) segura as
// chamadas até a pessoa entrar de novo, pelo popup ou com a senha.
//
// O cache do MSAL v5 é cifrado com uma chave guardada em cookie de sessão: fechar o navegador apaga
// contas e refresh tokens. Por isso o Rocky marca por conta própria que a sessão veio do Entra
// (SESSAO_ENTRA_KEY) e, sem conta no cache, pede ao Entra pela dica de login (o e-mail). A sessão do
// Entra é um cookie persistente no login.microsoftonline.com e sobrevive.
//
// O Entra continua sendo a autoridade: conta desativada lá não renova, e a troca no backend recusa
// usuário inativo no Rocky. Login local (senha) não passa pelo Entra e segue sem renovação.

/** Renova quando faltar menos que isso para o JWT vencer. */
export const ANTECEDENCIA_RENOVACAO_MS = 60 * 60 * 1000;

/** Intervalo entre tentativas depois de uma falha: não martela o Entra a cada foco na aba. */
export const INTERVALO_NOVA_TENTATIVA_MS = 5 * 60 * 1000;

/**
 * Intervalo mínimo entre renovações que deram certo. Sem isto a aba entraria em laço sempre que um
 * token recém-emitido já parecesse vencido ou perto de vencer: validade do JWT menor que a
 * antecedência, ou relógio da máquina horas adiantado em relação ao servidor.
 */
const INTERVALO_MINIMO_RENOVACAO_MS = 5 * 60 * 1000;

/** Uma retomada por redirect a cada janela: se o Entra devolver erro, o app não entra em laço. */
const JANELA_RETOMADA_MS = 2 * 60 * 1000;

/** Token renovado há menos que isso e mesmo assim recusado pelo servidor: o 401 não é de sessão. */
const JANELA_TOKEN_RECEM_RENOVADO_MS = 2 * 60 * 1000;

const CHAVE_RETORNO = 'rocky_sso_retorno';
const CHAVE_RETOMADA = 'rocky_sso_retomada_ts';
const LOCK_RENOVACAO = 'rocky-renovar-sessao';

export interface SessaoRenovada {
  token: string;
  user: AuthSessao;
}

/** Instante (ms) em que o JWT vence, ou null se não der para ler. */
export function expiracaoDoToken(token: string | null): number | null {
  if (!token) return null;
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/** O JWT já venceu. Token ilegível não conta: quem decide nesse caso é o 401 do backend. */
export function tokenVencido(token: string | null): boolean {
  const exp = expiracaoDoToken(token);
  return exp !== null && exp <= Date.now();
}

/** O JWT vence dentro da antecedência de renovação, ou já venceu. */
export function precisaRenovar(token: string | null): boolean {
  const exp = expiracaoDoToken(token);
  return exp !== null && exp - Date.now() <= ANTECEDENCIA_RENOVACAO_MS;
}

/** Marca a sessão como vinda do Entra (login com Microsoft ou renovação). O logout apaga. */
export function marcarSessaoViaEntra(email: string): void {
  localStorage.setItem(SESSAO_ENTRA_KEY, email.trim().toLowerCase());
}

/** Desfaz a marca: a sessão passou a ser de login com senha. */
export function desmarcarSessaoViaEntra(): void {
  localStorage.removeItem(SESSAO_ENTRA_KEY);
}

type IdentificacaoNoEntra = { account: AccountInfo } | { loginHint: string };

/**
 * Como pedir ao Entra pela pessoa da sessão: a conta do cache do MSAL com o mesmo e-mail ou, sem
 * ela (navegador reaberto apaga o cache), só a dica de login, desde que a sessão tenha vindo do
 * Entra. Em ambos o Entra só devolve essa pessoa: numa máquina compartilhada, a aba nunca troca de
 * usuário. Login com senha não tem como renovar: null.
 */
function identificacaoNoEntra(usuario: AuthSessao | null): IdentificacaoNoEntra | null {
  const email = usuario?.email?.trim().toLowerCase();
  if (!email) return null;
  const conta = msalInstance.getAllAccounts().find((c) => c.username.toLowerCase() === email);
  if (conta) return { account: conta };
  return localStorage.getItem(SESSAO_ENTRA_KEY) === email ? { loginHint: email } : null;
}

/** A sessão atual pode ser renovada pelo Entra (entrou com Microsoft, não com senha). */
export function podeRenovarPeloEntra(): boolean {
  return identificacaoNoEntra(authService.getStoredUser()) !== null;
}

let renovacaoEmAndamento: Promise<SessaoRenovada | null> | null = null;
let ultimaRenovacao = 0;
let ultimoTokenRenovado: string | null = null;
let ultimaFalha = 0;

/** Grava a sessão que o login devolveu, conferindo que é o mesmo usuário da aba. */
function gravarSessao(data: LoginResponse, usuario: AuthSessao, viaEntra = true): SessaoRenovada {
  if (data.user.id !== usuario.id) throw new Error('A conta usada é de outro usuário.');
  // O login devolve UserInfo, sem os campos que só o /me traz: mantém os que a sessão já tinha.
  const user: AuthSessao = { ...usuario, ...data.user };
  authService.setToken(data.token);
  authService.setUser(user);
  if (viaEntra) marcarSessaoViaEntra(user.email);
  else desmarcarSessaoViaEntra();
  ultimoTokenRenovado = data.token;
  ultimaRenovacao = Date.now();
  ultimaFalha = 0;
  return { token: data.token, user };
}

/**
 * Troca o JWT por um novo sem interação. Devolve a sessão renovada, ou null quando não deu: login
 * com senha, sessão do Entra inalcançável (Safari, sessão vencida), ou o backend recusou.
 *
 * Uma renovação por vez nesta aba (a promessa é compartilhada) e entre abas (Web Locks): a aba que
 * esperou o lock encontra o token que a outra acabou de gravar e não renova de novo.
 *
 * `forcar` é para depois de um 401: o servidor recusou `tokenRecusado`, então renova sem olhar o
 * relógio local nem o intervalo mínimo. Só a espera depois de uma falha continua valendo.
 */
export function renovarSessao(opcoes: { forcar?: boolean; tokenRecusado?: string } = {}): Promise<SessaoRenovada | null> {
  if (!renovacaoEmAndamento) {
    const executar = () => renovarAgora(opcoes.forcar ?? false, opcoes.tokenRecusado);
    const comLock = 'locks' in navigator ? navigator.locks.request(LOCK_RENOVACAO, executar) : executar();
    renovacaoEmAndamento = comLock.finally(() => {
      renovacaoEmAndamento = null;
    });
  }
  return renovacaoEmAndamento;
}

async function renovarAgora(forcar: boolean, tokenRecusado?: string): Promise<SessaoRenovada | null> {
  const token = authService.getToken();
  const usuario = authService.getStoredUser();
  if (!token || !usuario) return null;
  if (forcar) {
    // Outra aba trocou o token recusado enquanto esta esperava o lock: basta repetir com ele.
    if (tokenRecusado && token !== tokenRecusado) return { token, user: usuario };
  } else if (!precisaRenovar(token) || Date.now() - ultimaRenovacao < INTERVALO_MINIMO_RENOVACAO_MS) {
    // Outra aba renovou enquanto esta esperava o lock, ou esta acabou de renovar.
    return { token, user: usuario };
  }
  if (Date.now() - ultimaFalha < INTERVALO_NOVA_TENTATIVA_MS) return null;

  const identificacao = identificacaoNoEntra(usuario);
  if (!identificacao) return null;

  try {
    const { idToken } =
      'account' in identificacao
        ? await msalInstance.acquireTokenSilent({
            scopes: loginRequest.scopes,
            account: identificacao.account,
            // Pula o cache (precisa de um id_token novo): refresh token e, se ele já venceu, iframe.
            cacheLookupPolicy: CacheLookupPolicy.RefreshTokenAndNetwork
          })
        : // Sem conta no cache: direto ao iframe, com a sessão do Entra.
          await msalInstance.ssoSilent({ scopes: loginRequest.scopes, loginHint: identificacao.loginHint });
    return gravarSessao(await authService.ssoExchange(idToken), usuario);
  } catch (e) {
    ultimaFalha = Date.now();
    console.warn('[Sessão] renovação pelo Entra falhou', e);
    return null;
  }
}

/**
 * Renovação com a pessoa no controle: popup do Entra, aberto pelo clique em "Continuar conectado".
 * Com a sessão do Entra viva, o popup confirma e fecha sozinho; senão, pede o login ali mesmo.
 *
 * O Safari só abre popup durante o clique: nada pode ser aguardado antes do acquireTokenPopup, que
 * abre a janela na hora (navigatePopups, padrão do MSAL v5).
 */
export async function renovarSessaoComPopup(): Promise<SessaoRenovada> {
  const usuario = authService.getStoredUser();
  if (!usuario) throw new Error('Não há sessão para renovar.');
  const { idToken } = await msalInstance.acquireTokenPopup({
    scopes: loginRequest.scopes,
    ...(identificacaoNoEntra(usuario) ?? { loginHint: usuario.email })
  });
  return gravarSessao(await authService.ssoExchange(idToken), usuario);
}

/**
 * Step-up de quem entra por SSO: a pessoa re-autentica na Microsoft agora e o id_token que volta é
 * a prova de presença que o backend confere (ver `IReautenticacao`). Não troca a sessão — o JWT
 * continua o mesmo —, só prova quem está na frente da tela.
 *
 * `prompt: 'login'` é o ponto todo: com a sessão do Entra viva o popup confirmaria sozinho, e um
 * notebook deixado aberto aprovaria um Pix sem ninguém. Com ele, o Entra pede credencial (e MFA,
 * quando o tenant exige) de novo.
 *
 * Como qualquer popup do MSAL, tem de ser chamado **durante o clique** (o Safari bloqueia popup
 * aberto depois de um await).
 */
export async function confirmarPresencaNoEntra(): Promise<string> {
  const usuario = authService.getStoredUser();
  if (!usuario) throw new Error('Não há sessão para confirmar.');
  const { idToken } = await msalInstance.acquireTokenPopup({
    scopes: loginRequest.scopes,
    prompt: 'login',
    ...(identificacaoNoEntra(usuario) ?? { loginHint: usuario.email })
  });
  return idToken;
}

/**
 * Retomada de quem entra com senha, pelo diálogo de sessão encerrada: o mesmo e-mail e a senha de
 * novo, sem sair da página.
 */
export async function reentrarComSenha(senha: string): Promise<SessaoRenovada> {
  const usuario = authService.getStoredUser();
  if (!usuario) throw new Error('Não há sessão para retomar.');
  return gravarSessao(await authService.login(usuario.email, senha), usuario, false);
}

/**
 * Depois de um 401 numa chamada (authService.comSessao). O servidor recusou o token: renova à força,
 * sem olhar o relógio local. Se não der, abre o diálogo de sessão encerrada e espera a pessoa entrar
 * de novo ou sair. Devolve o token para repetir a chamada; o próprio token usado quando ele acabou de
 * ser renovado (o 401 não é de sessão, e renovar de novo não resolveria); ou null quando a sessão
 * acabou.
 */
async function recuperarSessaoApos401(tokenUsado: string | null): Promise<string | null> {
  const atual = authService.getToken();
  // Sem sessão, ou sessão encerrada (logout) enquanto a chamada ia: não há o que recuperar.
  if (!tokenUsado || !atual) return null;
  // Outra chamada ou outra aba já trocou o token: basta repetir com ele.
  if (atual !== tokenUsado) return atual;
  if (tokenUsado === ultimoTokenRenovado && Date.now() - ultimaRenovacao < JANELA_TOKEN_RECEM_RENOVADO_MS) {
    return tokenUsado;
  }
  const renovada = await renovarSessao({ forcar: true, tokenRecusado: tokenUsado });
  if (renovada) return renovada.token;
  return aguardarReautenticacao();
}

registrarRecuperacaoDe401(recuperarSessaoApos401);

/**
 * Último degrau, para quando o JWT já venceu e a renovação silenciosa não deu: redirect de
 * primeiro nível ao Entra com prompt=none, guardando a rota atual. Devolve true quando a navegação
 * começou; false quando não dá para tentar (login com senha, ou já tentou há pouco). Aí quem chamou
 * segue para a tela de login.
 */
export async function retomarSessaoPeloEntra(): Promise<boolean> {
  const identificacao = identificacaoNoEntra(authService.getStoredUser());
  if (!identificacao) return false;
  const ultimaTentativa = Number(sessionStorage.getItem(CHAVE_RETOMADA) || 0);
  if (Date.now() - ultimaTentativa < JANELA_RETOMADA_MS) return false;

  sessionStorage.setItem(CHAVE_RETOMADA, String(Date.now()));
  guardarRetornoAposLogin(`${window.location.pathname}${window.location.search}`);
  try {
    await msalInstance.loginRedirect({ ...loginRequest, ...identificacao, prompt: 'none' });
    return true;
  } catch (e) {
    console.warn('[Sessão] redirect silencioso ao Entra falhou', e);
    return false;
  }
}

/**
 * Guarda para onde voltar depois do redirect do Entra: a volta cai no /auth/callback, e o bootstrap
 * levava sempre para '/'. Caminho vazio, '/' ou das telas de login apaga o que estiver guardado.
 */
export function guardarRetornoAposLogin(caminho: string): void {
  const util =
    caminho.startsWith('/') &&
    !caminho.startsWith('//') &&
    caminho !== '/' &&
    !caminho.startsWith('/login') &&
    !caminho.startsWith('/auth/');
  if (util) sessionStorage.setItem(CHAVE_RETORNO, caminho);
  else sessionStorage.removeItem(CHAVE_RETORNO);
}

/** Lê e apaga o destino guardado. Só aceita caminho interno do app; sem destino, '/'. */
export function consumirRetornoAposLogin(): string {
  const caminho = sessionStorage.getItem(CHAVE_RETORNO);
  sessionStorage.removeItem(CHAVE_RETORNO);
  return caminho && caminho.startsWith('/') && !caminho.startsWith('//') ? caminho : '/';
}

/** O state da resposta do Entra na URL (no fragmento, padrão do MSAL, ou na query), ou null. */
function stateDaRespostaDoEntra(): string | null {
  for (const parte of [window.location.hash, window.location.search]) {
    const params = new URLSearchParams(parte.slice(1));
    if (params.has('state') && (params.has('code') || params.has('error'))) return params.get('state');
  }
  return null;
}

/**
 * A página é a volta do Entra dentro do iframe da renovação silenciosa ou do popup do "Continuar
 * conectado". No MSAL v5 essas janelas não processam a resposta: o index.tsx a repassa para a aba
 * do app pelo redirect-bridge e não sobe o app.
 *
 * O tipo de interação vem do state que o próprio MSAL montou (base64 de {id, meta}). window.opener
 * não serve para reconhecer o popup: a página de login da Microsoft corta essa referência. O login
 * por redirect segue com o handleRedirectPromise do bootstrap.
 */
export function ehVoltaDeJanelaAuxiliarDoMsal(): boolean {
  const state = stateDaRespostaDoEntra();
  if (state === null) return false;
  if (window.parent !== window) return true;
  try {
    const base64 = state.split('|')[0].replace(/ /g, '+').replace(/-/g, '+').replace(/_/g, '/');
    const { meta } = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')));
    return meta?.interactionType === 'popup' || meta?.interactionType === 'silent';
  } catch {
    return false;
  }
}
